import React, { useEffect, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, Chip, Typography,
  IconButton, Avatar, Grid, Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Edit, LockOpen } from '@mui/icons-material';
import axios from 'axios';
import { motion } from 'framer-motion';
import LoadingScreen from './LoadingScreen';
const AdminUserGrid = () => {
    const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const token = localStorage.getItem('token');
  useEffect(() => {
      const fetchData = async () => {
        try {
          await Promise.all([fetchUsers()]);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }, []);
  const fetchUsers = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`);
    setUsers(res.data);
  };

  const handleUnlock = async (id) => {
    try {
          await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/unlock/${id}`,{},{
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
     alert('User account unlocked')
    } catch (error) {
      alert('Not able to unlock User account')
    }

    fetchUsers();
  };

  const handleSave = async () => {
    setIsLoading(true);
    await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${editUser.id}`, editUser,{
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
    setEditUser(null);
    fetchUsers();
    setIsLoading(false);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditUser(prev => ({ ...prev, profile_photo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const columns = [
    {
      field: 'profile_photo',
      headerName: '',
      width: 60,
      renderCell: (params) => (
        <Avatar src={params.row.profile_photo} alt="P" sx={{ width: 36, height: 36 }} />
      ),
      sortable: false,
      filterable: false
    },
    { field: 'first_name', headerName: 'First Name', width: 130 },
    { field: 'last_name', headerName: 'Last Name', width: 130 },
    { field: 'email', headerName: 'Email', width: 180 },
    { field: 'company', headerName: 'Company', width: 130 },
    { field: 'role', headerName: 'Role', width: 120 },
    { field: 'stack_holder', headerName: 'Founder', width: 120 },
    {
      field: 'helpfields',
      headerName: 'Help Options',
      width: 220,
      renderCell: (params) =>
        params.value?.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {JSON.parse(params.value).map((h, idx) => (
              <Chip key={idx} label={h} size="small" color="success" />
            ))}
          </Box>
        ) : null,
    },
    {
      field: 'supporter_lst',
      headerName: 'Support Roles',
      width: 180,
      renderCell: (params) =>
        params.value?.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {JSON.parse(params.value).map((s, idx) => (
              <Chip key={idx} label={s} size="small" color="info" />
            ))}
          </Box>
        ) : null,
    },
   {
    field: 'status',
    headerName: 'Status',
    width: 150,
    renderCell: (params) => {
        const status = params.value;

        const statusColorMap = {
        unlocked: '#81C784',         // green
        activated: '#4FC3F7',        // blue
        approved: '#9575CD',         // purple
        pending_approval: '#FFB74D', // orange
        };

        return (
        <Chip
            label={status.replace('_', ' ').toUpperCase()}
            style={{
            backgroundColor: statusColorMap[status] || '#E0E0E0',
            color: '#fff',
            fontWeight: 500,
            }}
            size="small"
        />
        );
    },
    },

    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      renderCell: (params) => (
        <>
          <Tooltip title="View Details">
            <IconButton onClick={() => setViewUser(params.row)}><Visibility /></IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={() => setEditUser({ ...params.row })}><Edit /></IconButton>
          </Tooltip>
          <Tooltip title="Unlock">
            <IconButton onClick={() => handleUnlock(params.row.id)}><LockOpen /></IconButton>
          </Tooltip>
        </>
      ),
      sortable: false,
      filterable: false,
    }
  ];

   return isLoading ? <LoadingScreen /> : (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>User Management</Typography>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
        <DataGrid
          autoHeight
          rows={users}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={10}
          rowsPerPageOptions={[10, 20]}
          sx={{
            borderRadius: 3,
            boxShadow: 2,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5' },
            '& .MuiDataGrid-cell': { lineHeight: '20px' },
          }}
        />
      </motion.div>

      {/* View Dialog */}
      <Dialog open={!!viewUser} onClose={() => setViewUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>View User Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {viewUser && Object.entries(viewUser).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  label={key}
                  value={typeof value === 'object' ? JSON.stringify(value) : value}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  margin="dense"
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          {editUser && (
            <>
              {['status', 'company', 'industry', 'role'].map(field => (
                <TextField
                  key={field}
                  label={field}
                  value={editUser[field] || ''}
                  onChange={(e) => setEditUser({ ...editUser, [field]: e.target.value })}
                  fullWidth
                  margin="dense"
                />
              ))}
              {['accesslevel', 'isprovider', 'stack_holder'].map((field) => {
                const isBooleanField = ['isprovider', 'stack_holder'].includes(field);
                const options = isBooleanField
                    ? [
                        { value: 1, label: 'Yes' },
                        { value: 0, label: 'No' },
                    ]
                    : [
                        { value: 'admin', label: 'Admin' },
                        { value: 'user', label: 'User' },
                    ];

                return (
                    <TextField
                    key={field}
                    select
                    label={field=='stack_holder'?'Founder':field.replace(/_/g, ' ').toUpperCase()}
                    value={editUser[field] ?? ''}
                    onChange={(e) =>
                        setEditUser({ ...editUser, [field]: isBooleanField ? Number(e.target.value) : e.target.value })
                    }
                    fullWidth
                    margin="dense"
                    >
                    {options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                        </MenuItem>
                    ))}
                    </TextField>
                );
                })}
                <Button variant="contained" component="label">
                  Upload Photo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handlePhotoUpload}
                  />
                </Button>
              {editUser.profile_photo && (
                <Box mt={2}>
                  <img
                    src={editUser.profile_photo}
                    alt="Profile"
                    style={{ width: 80, height: 80, borderRadius: 10 }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserGrid;
